/* eslint-disable */
import axios, { AxiosResponse } from "axios";
import { stringify } from "qs";
import { compose, flatten, join, reject, isNil, isEmpty } from "ramda";
import { SessionState } from "../store/session/types";
import { get } from "lodash";
import { clearStorage, dirty } from "../store/session/actions";
import { toast } from "react-toastify";
const apiEndpoint = process.env.DMG_HOST_ENDPOINT || "/";
const baseUrl = `${apiEndpoint}`;

export function isString(value: unknown): boolean {
  return typeof value === "string" || value instanceof String;
}

export function isJson(str: string): boolean {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

let instance = axios.create({
  withCredentials: false,
  paramsSerializer: (params: any) =>
    stringify(params, { arrayFormat: "repeat" }),
  headers: {
    'x-api-key': process.env.ApiProxyKey as string
  }
});
const getResourceNameFromUrl = (url: string) => {
    return url.replace(`${baseUrl}/`, '');
}
const showAccessDeniedMessage = (message: string) => {
  toast.error(message, {
    closeOnClick: true,
  });
}
instance.interceptors.response.use(
  (response) => {
    const ERROR_CODE =
      get(response, "data.errors[0].code") ?? get(response, "data.errors.code");
    if (["USER-TOKEN", "USER-AUTH", "AUTHENTICATE-FAIL"].includes(ERROR_CODE)) {
      // logout if token expired
      clearStorage();
      window.location.href = "/";
    }
    return response;
  },
  function (error) {
    if (error.response) {
      const ERROR_CODE =
        get(error.response, "data.errors[0].code") ?? get(error.response, "data.errors.code");
      const HTTP_STATUS = error?.response?.status;
      if (["USER-TOKEN", "USER-AUTH", "AUTHENTICATE-FAIL"].includes(ERROR_CODE)) {
        // logout if token expired
        clearStorage();
        window.location.href = "/";
      }
      const url = error?.config?.url;
      if (url && HTTP_STATUS === 403) {
        const resource = getResourceNameFromUrl(url);
        const message = `Access denied to ${error.config.method} ${resource}`;
        showAccessDeniedMessage(message);
      }
      if (url && HTTP_STATUS === 408) {
        const message = `Request timeout to ${error.config.method} ${url}`;
        showAccessDeniedMessage(message);
      }
    }
    return error.response;
  }
);

export function configureWithSessionState(session: SessionState) {
  let token1 = null;
  if (session && session.data && session.data.meta) {
    token1 = session.data.meta.token;
  }

  if (isNil(token1) || isEmpty(token1) || session.isSecondUser) {
    instance = axios.create({
      withCredentials: false,
      paramsSerializer: (params: any) =>
        stringify(params, { arrayFormat: "repeat" }),
        headers: {
            'x-api-key': process.env.ApiProxyKey as string
        }
    });
  }

  if (!isNil(token1) && !isEmpty(token1)) {
    instance.interceptors.request.use(
      function (config) {
        const token1 = session.data?.meta?.token;

        let token2 = null;

        if (session.second != null) {
          token2 = session.second.data.meta.token;
        }

        const token = token2 === null ? token1 : token1 + "." + token2;

        if (config.headers) {
          config.headers["Authorization"] = "Bearer " + token;
          config.headers["x-api-key"] = process.env.ApiProxyKey;
        }

        return config;
      },
      function (error) {
        return Promise.reject(error);
      }
    );
  }
}

export type ResourceName =
  | "user"
  | "usergroup"
  | "access/collection"
  | "access/permission"
  | "merchant"
  | "processor"
  | "possoftware"
  | "pos"
  | "receipt"
  | "terminal"
  | "terminalimport"
  | "cert"
  | "card"
  | "cardType"
  | "company"
  | "cardPrompts"
  | "emv"
  | "mcc"
  | "stats"
  | "terminalLogos"
  | "terminal-background"
  | "auditTrail"
  | "applications"
  | "firmware"
  | "groups"
  | "htmlReceipt"
  | "terminal/bulkupdate"
  | "terminal/fetchBySerNo"
  | 'onboarding'
  | "onboarding/validate"
  | "onboarding/id"
  | "onboarding/import"
  | "offlinePaymentTypeGroup"
  | "hashedHotCard";

type ResourceAction = "login";
type ResourceId = number;
type ResourceFragment = ResourceName | ResourceId | ResourceAction;
type ResourcePath = Array<ResourceFragment | string>;

export interface InsertInstruction<T> {
  nestedResourcePath: ResourcePath;
  id: string | null | undefined;
  data: T;
}

export interface FetchInstruction<T> {
  nestedResourcePath: ResourcePath;
  id?: string | null | undefined;
  params?: T;
}

export interface DropInstruction {
  nestedResourcePath: ResourcePath;
  id: string | null | undefined;
}

const buildUrl = compose(
  // (url: string) => url + "/",
  (url: string) => url,
  join("/"),
  reject(isNil) as (
    x: Array<ResourceFragment | string | number | null | undefined>
  ) => Array<ResourceFragment | string | number>,
  flatten as (
    x: Array<ResourcePath | string | number | null | undefined>
  ) => Array<ResourceFragment | string | null | undefined>
);

export function insert<T>({
                            nestedResourcePath,
                            id,
                            data,
                          }: InsertInstruction<T>): Promise<AxiosResponse<any>> {
  const url = buildUrl([baseUrl, nestedResourcePath, id]);
  return (!!id ? instance.patch : instance.post)(url, data);
}

export function fetch<T>({
  nestedResourcePath,
  id,
  params,
}: FetchInstruction<T>): Promise<AxiosResponse<any>> {
  const url = buildUrl([baseUrl, nestedResourcePath, id]);

  return instance.get(url, {
    params: params
  });
}

export function fetchApplication<T>({
  nestedResourcePath,
  params,
}: FetchInstruction<T>): Promise<AxiosResponse<any>> {
  const url = buildUrl([baseUrl, nestedResourcePath]);
  return instance.get(url, { params: params });
}

export function drop({
  nestedResourcePath,
  id,
}: DropInstruction): Promise<AxiosResponse<any>> {
  const url = buildUrl([baseUrl, nestedResourcePath, id]);
  return instance.delete(url);
}

/*
 * These were hacked in, would be worthwhile to conform these to the above functions.
 * */
export async function getTerminalCommandsForTID(tid: string) {
  console.log(baseUrl + "/TerminalCommand?TID=" + tid);
  return instance.get(baseUrl + "/TerminalCommand?TID=" + tid);
}

export async function postCreateTerminalCommand(tid: string, body: object) {
  console.log(baseUrl + "/TerminalCommand/create?TID=" + tid);
  return instance.post(baseUrl + "/TerminalCommand/create?TID=" + tid, body);
}

export async function postCreateTerminalCommandForAll(body: object) {
  return instance.post(baseUrl + "/TerminalCommand/create?ALL", body);
}

export async function getTerminalCommandById(id: string) {
  return instance.get(baseUrl + "/TerminalCommand/" + id);
}

export async function deleteTerminalCommandById(id: string) {
  return instance.delete(baseUrl + "/TerminalCommand/" + id);
}

/*
 * For Bulk Update Feature
 * */
export type GeneralDataItem = {
  id: string;
  type: string;
  [key: string]: any;
};

export interface FetchResult {
  data: GeneralDataItem[];
  meta?: Record<string, unknown>;
}

export interface BulkUpdateParams {
  model: string;
  filter: { [key: string]: string };
  updatedData: { [key: string]: any };
  excludedIds: string[];
  terminalType?: string;
}

export const fetchFilteredDataByModel = async (
  model: ResourceName,
  params?: {}
) => {
  return fetch({
    nestedResourcePath: [model],
    id: null,
    params: params,
  }).then((response) => response.data as FetchResult);
};

export const bulkUpdate = async (body: BulkUpdateParams) => {
  return instance.patch(baseUrl + `/${body.model}/bulkupdate`, body);
};
